import { Box, Chip, InputBase } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import theme from "lib/theme";
import {
  commitDraft,
  formatAssetSearch,
  insertPastedLines,
  parseAssetSearchQuery,
  termsFromLineList,
} from "./assetSearchQuery";

const PLACEHOLDER = "Search matches on serial number, model...";
const CHIP_ROW_HEIGHT = 26;
const ROW_GAP = 6;
const FIELD_PADDING_Y = 4;
const FIELD_BORDER = 1;
const VISIBLE_ROWS = 2;
const MAX_FIELD_HEIGHT =
  FIELD_BORDER * 2 + FIELD_PADDING_Y * 2 + CHIP_ROW_HEIGHT * VISIBLE_ROWS + ROW_GAP * (VISIBLE_ROWS - 1);

type AssetSearchFieldProps = {
  value: string;
  onChange: (value: string) => void;
};

export default function AssetSearchField({ value, onChange }: AssetSearchFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [{ terms, draft }, setSearch] = useState(() => parseAssetSearchQuery(value));
  const emitted = useRef(formatAssetSearch(terms, draft));
  const applyingUrlValue = useRef(false);
  const awaitingUrlEcho = useRef(false);

  useEffect(() => {
    if (applyingUrlValue.current) {
      applyingUrlValue.current = false;
      return;
    }

    const next = formatAssetSearch(terms, draft);
    if (next === emitted.current) return;

    emitted.current = next;
    awaitingUrlEcho.current = true;
    onChange(next);
  }, [terms, draft, onChange]);

  useEffect(() => {
    if (value === emitted.current) {
      awaitingUrlEcho.current = false;
      return;
    }

    // q is debounced, so an older write can land after a newer edit.
    if (awaitingUrlEcho.current) return;

    const next = parseAssetSearchQuery(value);
    emitted.current = formatAssetSearch(next.terms, next.draft);
    applyingUrlValue.current = true;
    setSearch(next);
  }, [value]);

  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;

    field.scrollTop = field.scrollHeight;
  }, [terms]);

  return (
    <Box
      ref={fieldRef}
      data-testid="asset-search-field"
      style={{ maxHeight: MAX_FIELD_HEIGHT, overflowY: "auto", boxSizing: "border-box", overscrollBehavior: "contain" }}
      onClick={() => inputRef.current?.focus()}
      sx={{
        flex: 1,
        minWidth: 0,
        maxWidth: 500,
        mr: 1,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: `${ROW_GAP}px`,
        px: 1,
        py: `${FIELD_PADDING_Y}px`,
        minHeight: 38,
        bgcolor: "#fff",
        border: `${FIELD_BORDER}px solid rgb(206, 212, 218)`,
        borderRadius: "4px",
        cursor: "text",
        "&:hover, &:focus-within": { zIndex: 2, borderColor: theme.palette.primary.main },
      }}
    >
      {terms.map((term) => (
        <Chip
          key={term.toLowerCase()}
          label={term}
          size="small"
          onDelete={() =>
            setSearch((current) => ({
              ...current,
              terms: current.terms.filter((existing) => existing.toLowerCase() !== term.toLowerCase()),
            }))
          }
          onClick={(event) => event.stopPropagation()}
          sx={{
            bgcolor: "#e9ecef",
            height: 26,
            borderRadius: 999,
            maxWidth: "100%",
            "& .MuiChip-label": { px: 1.25, fontSize: 14, color: theme.palette.text.primary },
            "& .MuiChip-deleteIcon": { fontSize: 16, color: theme.palette.text.secondary },
          }}
        />
      ))}
      <InputBase
        inputRef={inputRef}
        value={draft}
        placeholder={terms.length === 0 ? PLACEHOLDER : ""}
        autoComplete="off"
        onChange={(event) => {
          setSearch((current) => ({ ...current, draft: event.target.value }));
        }}
        onPaste={(event) => {
          const text = event.clipboardData.getData("text");
          if (!/[\r\n]/.test(text)) return;

          event.preventDefault();
          const input = inputRef.current;

          setSearch((current) => {
            const value = input?.value ?? current.draft;
            const start = input?.selectionStart ?? value.length;
            const end = input?.selectionEnd ?? value.length;

            return {
              terms: termsFromLineList(insertPastedLines(value, text, start, end), current.terms),
              draft: "",
            };
          });
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            if (!draft.trim()) return;

            setSearch({ terms: commitDraft(terms, draft), draft: "" });
            return;
          }

          if (event.key === "Backspace" && draft === "" && terms.length > 0) {
            event.preventDefault();
            setSearch((current) => ({ ...current, terms: current.terms.slice(0, -1) }));
          }
        }}
        inputProps={{ "aria-label": "Search assets", "data-testid": "asset-search-input" }}
        sx={{ flex: 1, minWidth: 120, fontSize: 16 }}
      />
    </Box>
  );
}
